import React, { useState, useRef, useEffect, useMemo } from "react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import {
  PLACEHOLDER_REGO,
  PLACEHOLDER_MAKE,
  PLACEHOLDER_MODEL,
  formatModelForDisplay,
  normalizeRego,
  normalizeMake,
  normalizeModel,
  vehicleHasPlaceholderDetails,
  vinLooksPlaceholder,
  makeLooksPlaceholder,
  modelLooksPlaceholder,
} from "../utils/vehiclePlaceholders";
import { fetchVehicleDetails } from "../utils/fetchVehicleDetails";
import { getFieldsFromNevdisDetails } from "../utils/mapNevdisBodyType";
import {
  canonicalizeVehicleState,
  getVehicleStateFieldLabel,
  getVehicleStateOptionsForSelect,
  isAuVehicleState
} from "../utils/p1Region";

function resolveAuLookupState(vehicle, customer) {
  const candidates = [
    vehicle?.state,
    customer?.address?.state,
    customer?.address?.State,
  ];
  for (const raw of candidates) {
    const canonical = canonicalizeVehicleState(raw);
    if (isAuVehicleState(canonical)) return canonical;
  }
  return "";
}

function pickMergedField(override, base, key) {
  if (override && Object.prototype.hasOwnProperty.call(override, key)) {
    return override[key];
  }
  return base?.[key];
}

function mergeVehicleSnapshot(base, override) {
  if (!base) return null;
  if (!override) return base;
  return {
    ...base,
    // Honor explicit null/"" so clearing a placeholder VIN does not fall back
    // to DEFAULTVINNUMBERS and keep the confirm-vehicle gate open.
    rego: pickMergedField(override, base, "rego"),
    make: pickMergedField(override, base, "make"),
    model: pickMergedField(override, base, "model"),
    vin: pickMergedField(override, base, "vin"),
    state: pickMergedField(override, base, "state"),
  };
}

function getContractEntryId(entry) {
  const c = entry?.contractData;
  if (!c) return "";
  return String(c._id || c.contractId || "");
}

function getAgreementListLabel(entry) {
  const vehicle = entry?.contractData?.vehicle;
  const rego =
    vehicle?.rego && normalizeRego(vehicle.rego) !== PLACEHOLDER_REGO
      ? vehicle.rego
      : "Vehicle pending";
  const product =
    entry?.contractData?.product?.name ||
    entry?.subscription?.product?.name ||
    "Membership";
  return { rego, product };
}

const ContractAcceptanceModal = ({
  isOpen,
  onClose,
  contractData: singleContractData,
  pendingContracts,
  onContractAccepted,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSignature, setAcceptedSignature] = useState(false);

  const [vehicleDetailsOverride, setVehicleDetailsOverride] = useState(null);
  const [regoInput, setRegoInput] = useState("");
  const [stateInput, setStateInput] = useState("");
  const [makeInput, setMakeInput] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [vinInput, setVinInput] = useState("");
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [isVehicleLookupLoading, setIsVehicleLookupLoading] = useState(false);
  const [lookupSettledContractId, setLookupSettledContractId] = useState("");

  const canvasRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const eventHandlersRef = useRef(null);

  const [queueOffset, setQueueOffset] = useState(0);
  const [completedContractIds, setCompletedContractIds] = useState([]);

  const contractQueue = useMemo(() => {
    if (Array.isArray(pendingContracts) && pendingContracts.length > 0) {
      return pendingContracts;
    }
    if (singleContractData) {
      return [{ contractData: singleContractData }];
    }
    return [];
  }, [pendingContracts, singleContractData]);

  const sessionTotal = contractQueue.length;
  const isMultiContractSession = sessionTotal > 1;
  const currentEntry = contractQueue[queueOffset] || null;
  const contractData = currentEntry?.contractData || null;
  const currentAgreementIndex = queueOffset + 1;
  const agreementsRemaining = sessionTotal - queueOffset;
  const isLastAgreement = agreementsRemaining <= 1;

  useEffect(() => {
    if (isOpen) {
      setQueueOffset(0);
      setCompletedContractIds([]);
    }
  }, [isOpen, sessionTotal, pendingContracts, singleContractData?._id, singleContractData?.contractId]);

  // Get contract ID - handle both _id and contractId for API compatibility
  const getContractId = () => {
    if (!contractData) return null;
    return contractData._id || contractData.contractId || null;
  };

  // Memoize contract ID for use in dependencies
  const contractId = useMemo(() => {
    if (!contractData) return null;
    return contractData._id || contractData.contractId || null;
  }, [contractData?._id, contractData?.contractId]);

  const contractStatusForGate = contractData?.contractStatus || contractData?.status;

  const effectiveVehicle = useMemo(
    () => mergeVehicleSnapshot(contractData?.vehicle, vehicleDetailsOverride),
    [contractData?.vehicle, vehicleDetailsOverride]
  );

  const needsVehicleDetailsGate = useMemo(() => {
    if (!isOpen || !contractData || !contractId) return false;
    if (contractStatusForGate && contractStatusForGate !== "pending") return false;
    return vehicleHasPlaceholderDetails(effectiveVehicle);
  }, [
    isOpen,
    contractData,
    contractId,
    contractStatusForGate,
    effectiveVehicle,
  ]);

  const needsAutoVehicleLookup = useMemo(() => {
    if (!isOpen || !contractData || !contractId || needsVehicleDetailsGate) return false;
    const vehicle = effectiveVehicle || contractData.vehicle;
    if (!vehicle || vehicleHasPlaceholderDetails(vehicle)) return false;
    if (!makeLooksPlaceholder(vehicle.make) && !modelLooksPlaceholder(vehicle.model)) {
      return false;
    }
    return Boolean(resolveAuLookupState(vehicle, contractData.customer));
  }, [
    isOpen,
    contractData,
    contractId,
    needsVehicleDetailsGate,
    effectiveVehicle,
  ]);

  const isHoldingContractForLookup =
    needsAutoVehicleLookup && lookupSettledContractId !== String(contractId || "");

  useEffect(() => {
    setVehicleDetailsOverride(null);
  }, [contractId]);

  useEffect(() => {
    if (!isOpen || !needsVehicleDetailsGate || !contractData?.vehicle) return;
    const v = contractData.vehicle;
    setRegoInput(normalizeRego(v.rego) === PLACEHOLDER_REGO ? "" : v.rego || "");
    setStateInput(
      canonicalizeVehicleState(v.state) ||
        canonicalizeVehicleState(contractData.customer?.address?.state)
    );
    setMakeInput(normalizeMake(v.make) === PLACEHOLDER_MAKE ? "" : v.make || "");
    setModelInput(
      normalizeModel(v.model) === PLACEHOLDER_MODEL ? "" : formatModelForDisplay(v.model)
    );
    setVinInput(vinLooksPlaceholder(v) ? "" : v.vin || "");
  }, [isOpen, needsVehicleDetailsGate, contractId, contractData?.vehicle, contractData?.customer]);

  useEffect(() => {
    if (!isOpen || !needsVehicleDetailsGate) return;
    const rego = regoInput.trim().toUpperCase();
    const state = canonicalizeVehicleState(stateInput);
    if (!rego || !state || !isAuVehicleState(state)) {
      setIsVehicleLookupLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsVehicleLookupLoading(true);
      try {
        const vehicleDetails = await fetchVehicleDetails(rego, state);
        if (cancelled || !vehicleDetails) return;
        setMakeInput((prev) => (prev.trim() ? prev : (vehicleDetails.make || "").toUpperCase()));
        setModelInput((prev) => (prev.trim() ? prev : (vehicleDetails.model || "").toUpperCase()));
        setVinInput((prev) => (prev.trim() ? prev : (vehicleDetails.vin || "").toUpperCase()));
      } catch (error) {
        // Lookup is best-effort only. User can still enter details manually.
      } finally {
        if (!cancelled) {
          setIsVehicleLookupLoading(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, needsVehicleDetailsGate, regoInput, stateInput]);

  useEffect(() => {
    if (!isOpen) {
      setLookupSettledContractId("");
      return;
    }
    if (!needsAutoVehicleLookup || !contractId) return;
    if (lookupSettledContractId === String(contractId)) return;

    const vehicle = effectiveVehicle || contractData?.vehicle;
    const vehicleId = vehicle?._id || contractData?.vehicle?._id || contractData?.vehicle;
    const rego = normalizeRego(vehicle?.rego);
    const state = resolveAuLookupState(vehicle, contractData?.customer);
    if (!vehicle || !vehicleId || !rego || !state) {
      setLookupSettledContractId(String(contractId));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const details = await fetchVehicleDetails(rego, state);
        if (cancelled || !details) return;

        const fields = getFieldsFromNevdisDetails(details);
        const payload = { rego, state };
        if (makeLooksPlaceholder(vehicle.make) && fields.make) {
          payload.make = String(fields.make).trim().toUpperCase();
        }
        if (modelLooksPlaceholder(vehicle.model) && fields.model) {
          payload.model = String(fields.model).trim().toUpperCase();
        }
        if (fields.vin) {
          payload.vin = fields.vin;
        } else if (vinLooksPlaceholder(vehicle)) {
          payload.vin = null;
        }
        if (fields.color) payload.color = fields.color;
        if (fields.modelYear) payload.modelYear = fields.modelYear;
        if (fields.vehicleType) payload.vehicleType = fields.vehicleType;

        const patch = await api(
          `v1/vehicle/${vehicleId}/updatedVehicleDetail`,
          "PATCH",
          payload
        );
        if (cancelled) return;

        let nextVehicle = null;
        if (patch.status === 200 && patch.data?.success) {
          const refreshed = await api(`v1/vehicle/${vehicleId}`, "GET");
          if (refreshed.status === 200 && refreshed.data?.data?.vehicle) {
            nextVehicle = refreshed.data.data.vehicle;
          }
        }

        if (cancelled) return;
        setVehicleDetailsOverride({
          rego: nextVehicle?.rego || rego,
          make: nextVehicle?.make || payload.make || vehicle.make,
          model: nextVehicle?.model || payload.model || vehicle.model,
          vin: nextVehicle?.vin || payload.vin || "",
          state: nextVehicle?.state || state,
        });
      } catch (error) {
        // Lookup is best-effort. Still show the agreement if NEVDIS is unavailable.
      } finally {
        if (!cancelled) {
          setLookupSettledContractId(String(contractId));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    contractId,
    contractData,
    needsAutoVehicleLookup,
    lookupSettledContractId,
    effectiveVehicle,
  ]);

  // Reset form state when contractData changes (new contract loaded)
  useEffect(() => {
    if (contractData) {
      setAcceptedTerms(false);
      setAcceptedSignature(false);
      setSignature("");
      setIsSubmitting(false);
      // Clear the signature canvas
      if (signatureCanvasRef.current) {
        const ctx = signatureCanvasRef.current.getContext("2d");
        if (ctx) {
          const rect = signatureCanvasRef.current.getBoundingClientRect();
          ctx.clearRect(0, 0, rect.width, rect.height);
        }
      }
      // Reset event handlers to allow re-initialization
      eventHandlersRef.current = null;
      console.log("🔄 Contract data changed, resetting form state", {
        contractId: contractId,
        hasId: !!contractData._id,
        hasContractId: !!contractData.contractId
      });
    }
  }, [contractId, contractData]);

  useEffect(() => {
    if (isOpen && !needsVehicleDetailsGate && !isHoldingContractForLookup && signatureCanvasRef.current) {
      // Clean up any existing handlers first
      cleanupSignatureCanvas();

      // Use a timeout to ensure the DOM is fully rendered
      // Increased timeout for first-time login scenarios where DOM might not be ready
      const timer = setTimeout(() => {
        // Double-check canvas is still available and has dimensions
        if (signatureCanvasRef.current) {
          const rect = signatureCanvasRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            initializeSignatureCanvas();
          } else {
            // Retry if canvas doesn't have dimensions yet
            console.log("Canvas not ready, retrying initialization...");
            setTimeout(() => {
              if (signatureCanvasRef.current) {
                initializeSignatureCanvas();
              }
            }, 100);
          }
        }
      }, 200); // Slightly longer timeout for first-time login

      return () => {
        clearTimeout(timer);
        // Clean up event listeners when component unmounts or modal closes
        cleanupSignatureCanvas();
      };
    }
  }, [isOpen, contractId, needsVehicleDetailsGate, isHoldingContractForLookup]); // Re-init after vehicle lookup completes

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupSignatureCanvas();
    };
  }, []);

  const cleanupSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas && eventHandlersRef.current) {
      const { startDrawing, draw, stopDrawing, touchStart, touchMove, touchEnd } = eventHandlersRef.current;

      // Remove mouse event listeners
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseout", stopDrawing);

      // Remove touch event listeners
      canvas.removeEventListener("touchstart", touchStart);
      canvas.removeEventListener("touchmove", touchMove);
      canvas.removeEventListener("touchend", touchEnd);

      eventHandlersRef.current = null;
      console.log("Signature canvas event listeners cleaned up");
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store the original overflow style
      const originalOverflow = document.body.style.overflow;
      // Lock scrolling
      document.body.style.overflow = "hidden";

      // Cleanup function to restore scrolling when modal closes
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const initializeSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      console.error("Signature canvas not found");
      return;
    }

    // Clean up existing handlers if they exist (for re-initialization)
    if (eventHandlersRef.current) {
      console.log("Cleaning up existing handlers before re-initialization");
      cleanupSignatureCanvas();
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Could not get 2D context from canvas");
      return;
    }

    // Clear the canvas first
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width || rect.width, canvas.height || rect.height);

    // Get the actual rendered size of the canvas
    // Use a small delay to ensure canvas is fully rendered
    const currentRect = canvas.getBoundingClientRect();
    if (currentRect.width === 0 || currentRect.height === 0) {
      console.warn("Canvas has zero dimensions, retrying initialization...");
      setTimeout(() => initializeSignatureCanvas(), 100);
      return;
    }

    // Set canvas internal dimensions to match the displayed size
    // Use device pixel ratio for crisp drawing on high-DPI displays
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = currentRect.width * devicePixelRatio;
    canvas.height = currentRect.height * devicePixelRatio;

    // Scale the context to match the device pixel ratio
    ctx.scale(devicePixelRatio, devicePixelRatio);

    // Set the CSS size back to the original size
    canvas.style.width = `${currentRect.width}px`;
    canvas.style.height = `${currentRect.height}px`;

    // Set drawing styles
    ctx.strokeStyle = "#10073E";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

      console.log("✅ Signature canvas initialized:", {
      displayWidth: currentRect.width,
      displayHeight: currentRect.height,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      devicePixelRatio: devicePixelRatio,
      strokeStyle: ctx.strokeStyle,
      lineWidth: ctx.lineWidth,
      contractId: contractId
    });

    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();

      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    const startDrawing = (e) => {
      isDrawing = true;
      const coords = getCoordinates(e);
      lastX = coords.x;
      lastY = coords.y;

      console.log("Starting to draw at:", coords);
    };

    const draw = (e) => {
      if (!isDrawing) return;

      const coords = getCoordinates(e);

      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      lastX = coords.x;
      lastY = coords.y;
    };

    const stopDrawing = () => {
      isDrawing = false;
      // Check if there's actually signature content by sampling some pixels
      // Use the actual canvas dimensions (accounting for device pixel ratio)
      // We need to sample from the scaled canvas, not the display size
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Check if any pixel has non-zero alpha (indicating a signature was drawn)
      // We check every 4th value (alpha channel) and also check RGB values for better detection
      const hasSignature = imageData.data.some(
        (value, index) => {
          const channel = index % 4;
          // Check if any channel (R, G, B, or A) has a value > 0
          // This is more reliable than just checking alpha
          return value > 0;
        }
      );

      if (hasSignature) {
        setAcceptedSignature(true);
        console.log("Signature drawing completed - signature detected", {
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          imageDataLength: imageData.data.length
        });
      } else {
        console.log("No signature detected in canvas");
      }
    };

    // Define touch event handlers
    const touchStart = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const coords = getCoordinates(touch);

      isDrawing = true;
      lastX = coords.x;
      lastY = coords.y;

      console.log("Touch start at:", coords);
    };

    const touchMove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;

      const touch = e.touches[0];
      const coords = getCoordinates(touch);

      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      lastX = coords.x;
      lastY = coords.y;
    };

    const touchEnd = (e) => {
      e.preventDefault();
      stopDrawing();
    };

    // Mouse events
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseout", stopDrawing);

    // Touch events for mobile
    canvas.addEventListener("touchstart", touchStart);
    canvas.addEventListener("touchmove", touchMove);
    canvas.addEventListener("touchend", touchEnd);

    // Store event handlers for cleanup
    eventHandlersRef.current = {
      startDrawing,
      draw,
      stopDrawing,
      touchStart,
      touchMove,
      touchEnd
    };
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      setAcceptedSignature(false);
      console.log("Signature cleared");
    }
  };

  const getSignatureData = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const signatureData = canvas.toDataURL();
      console.log("Signature captured:", {
        hasData: !!signatureData,
        dataLength: signatureData?.length || 0,
        isValid: signatureData && signatureData !== "data:," && signatureData.length > 100
      });
      return signatureData;
    }
    return "";
  };

  const handleSaveVehicleDetails = async () => {
    if (!contractData?.vehicle) {
      showErrorToast("Vehicle data is missing. Please refresh the page and try again.");
      return;
    }
    const vehicleId = contractData.vehicle._id || contractData.vehicle;
    const rego = regoInput.trim().toUpperCase();
    const make = makeInput.trim().toUpperCase();
    const model = modelInput.trim().toUpperCase();

    if (!rego) {
      showErrorToast("Please enter your vehicle registration.");
      return;
    }

    const state = canonicalizeVehicleState(stateInput);
    if (!state) {
      showErrorToast(`Please select a ${getVehicleStateFieldLabel().toLowerCase()}.`);
      return;
    }

    const payload = { rego, state };
    if (make) {
      payload.make = make;
    }
    if (model) {
      payload.model = model;
    }
    if (vinInput.trim()) {
      payload.vin = vinInput.trim();
    } else if (vinLooksPlaceholder(contractData?.vehicle)) {
      // Omitting `vin` is not enough for older APIs; clearing explicitly removes GEN-* / DEFAULTVIN from DB.
      payload.vin = null;
    }

    setSavingVehicle(true);
    try {
      const patch = await api(`v1/vehicle/${vehicleId}/updatedVehicleDetail`, "PATCH", payload);
      if (patch.status !== 200 || !patch.data?.success) {
        if (
          patch.data?.errorCode === "RECENT_CANCELLED_VEHICLE_SUBSCRIPTION" ||
          patch.data?.errorCode === "DUPLICATE_VEHICLE_PRODUCT"
        ) {
          const supportEmail = patch.data?.data?.supportEmail;
          const supportPhone = patch.data?.data?.supportPhone;
          const contact = [
            supportEmail ? `Email: ${supportEmail}` : "",
            supportPhone ? `Phone: ${supportPhone}` : "",
          ]
            .filter(Boolean)
            .join(". ");
          showErrorToast(contact ? `${patch.data.message} ${contact}.` : patch.data.message);
          return;
        }
        showErrorToast(patch.data?.message || "Could not update vehicle details.");
        return;
      }
      const refreshed = await api(`v1/vehicle/${vehicleId}`, "GET");
      if (refreshed.status !== 200 || !refreshed.data?.success || !refreshed.data.data?.vehicle) {
        showErrorToast("Vehicle was updated but could not load the latest details. Please refresh the page.");
        return;
      }
      const v = refreshed.data.data.vehicle;
      setVehicleDetailsOverride({
        rego: v.rego || rego,
        make: v.make || make,
        model: v.model || model,
        // Empty string (not null) so merge does not revive DEFAULTVINNUMBERS.
        vin: v.vin || "",
      });
      showSuccessToast("Vehicle details saved. You can now sign your agreement.");
    } catch (err) {
      console.error("Update vehicle before contract:", err);
      showErrorToast("Failed to update vehicle. Please try again.");
    } finally {
      setSavingVehicle(false);
    }
  };

  const handleAcceptContract = async () => {
    // Validate contractData exists before proceeding
    const currentContractId = getContractId();
    if (!contractData || !currentContractId) {
      console.error("ContractAcceptanceModal: Cannot accept contract - contractData is missing", {
        contractData,
        contractId,
        hasId: !!contractData?._id,
        hasContractId: !!contractData?.contractId
      });
      showErrorToast("Contract data is missing. Please refresh the page and try again.");
      return;
    }

    if (needsVehicleDetailsGate || vehicleHasPlaceholderDetails(effectiveVehicle)) {
      showErrorToast("Please confirm your real vehicle details before signing.");
      return;
    }

    if (!acceptedTerms || !acceptedSignature) {
      showErrorToast("Please accept the terms and provide your signature");
      return;
    }

    const signatureData = getSignatureData();
    if (!signatureData || signatureData === "data:," || signatureData.length < 100) {
      showErrorToast("Please provide a valid signature");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Submitting contract acceptance:", {
        contractId: currentContractId,
        hasSignature: !!signatureData,
        signatureLength: signatureData?.length || 0
      });

      const ipAddress = await getClientIP();
      console.log("Client IP address:", ipAddress);

      const response = await api("v1/contract/accept", "POST", {
        contractId: currentContractId,
        signature: signatureData,
        ipAddress: ipAddress,
        userAgent: navigator.userAgent
      });

      console.log("Contract acceptance response:", response);

      if (response.data && response.data.success) {
        const nextOffset = queueOffset + 1;
        if (nextOffset < sessionTotal) {
          setCompletedContractIds((prev) => [...prev, String(currentContractId)]);
          setQueueOffset(nextOffset);
          const left = sessionTotal - nextOffset;
          showSuccessToast(
            `Agreement signed. ${left} more to review in this session.`
          );
        } else {
          showSuccessToast(
            isMultiContractSession
              ? "All subscription agreements signed successfully!"
              : "Contract accepted successfully!"
          );
          onContractAccepted(response.data.data);
          onClose();
        }
      } else {
        const errorMessage = response.data?.message || "Failed to accept contract";
        console.error("Contract acceptance failed:", errorMessage);
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error accepting contract:", error);
      const errorMessage = error.response?.data?.message || "Failed to accept contract. Please try again.";
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getClientIP = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error("Error getting IP:", error);
      return "unknown";
    }
  };

  if (!isOpen) return null;

  // Don't render modal if contractData is missing
  if (!contractData || !contractId) {
    console.error("ContractAcceptanceModal: contractData is missing or invalid", {
      contractData,
      contractId,
      hasId: !!contractData?._id,
      hasContractId: !!contractData?.contractId
    });
    return null;
  }

  // Only show modal for pending contracts
  // Check both contractStatus and status fields for API compatibility
  const contractStatus = contractData.contractStatus || contractData.status;
  if (contractStatus && contractStatus !== "pending") {
    console.log("ContractAcceptanceModal: Contract is not pending, status:", contractStatus);
    return null;
  }

  const displayVehicle = effectiveVehicle || contractData.vehicle;

  return (
      <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        // Prevent closing when clicking outside - contract must be signed
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          // Don't call onClose - user must sign the contract
        }
      }}
      onKeyDown={(e) => {
        // Prevent closing with ESC key
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[min(95dvh,95vh)] my-2 sm:my-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#10073E] to-[#2D1B69] text-white p-3 sm:p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-xl sm:text-2xl font-bold">Priority One Wash Club</h2>
              <p className="text-base sm:text-lg">Subscription Agreement</p>
            </div>
          </div>
        </div>

        {isMultiContractSession && (
          <div className="px-3 sm:px-6 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Pending agreements ({sessionTotal})
            </p>
            <ul className="flex flex-col gap-1 max-h-20 overflow-y-auto">
              {contractQueue.map((entry, idx) => {
                const entryId = getContractEntryId(entry);
                const isDone =
                  completedContractIds.includes(entryId) || idx < queueOffset;
                const isCurrent = idx === queueOffset && !isDone;
                const { rego, product } = getAgreementListLabel(entry);
                return (
                  <li
                    key={entryId || idx}
                    className={`flex items-center justify-between gap-2 rounded-md px-3 py-1.5 text-sm ${
                      isCurrent
                        ? "bg-blue-100 border border-blue-300"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-gray-900">{rego}</span>
                      <span className="block text-xs text-gray-500 truncate">{product}</span>
                    </div>
                    <span
                      className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                        isDone
                          ? "bg-green-100 text-green-800"
                          : isCurrent
                            ? "bg-blue-600 text-white"
                            : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {isDone ? "Signed" : isCurrent ? "Current" : "Pending"}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs sm:text-sm font-medium text-gray-800 mt-1.5">
              Signing agreement {currentAgreementIndex} of {sessionTotal}
            </p>
          </div>
        )}

        {needsVehicleDetailsGate ? (
          <>
            <div className="flex-1 overflow-y-auto p-4 sm:p-8 max-w-xl mx-auto w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm your vehicle</h3>
              <p className="text-sm text-gray-600 mb-6">
                Your account still has placeholder vehicle details from setup. Please enter your registration before you
                review and sign the agreement. Make, model, and VIN are optional.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={regoInput}
                    onChange={(e) => setRegoInput(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {getVehicleStateFieldLabel()}
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    value={stateInput}
                    onChange={(e) => setStateInput(e.target.value)}
                  >
                    <option value="">Select {getVehicleStateFieldLabel().toLowerCase()}</option>
                    {getVehicleStateOptionsForSelect(stateInput).map((stateCode) => (
                      <option key={stateCode} value={stateCode}>
                        {stateCode}
                      </option>
                    ))}
                  </select>
                </div>
                {regoInput.trim() && stateInput.trim() && isAuVehicleState(stateInput) && (
                  <div className="flex items-center text-xs text-gray-600">
                    {isVehicleLookupLoading ? (
                      <>
                        <span className="inline-block w-3 h-3 mr-2 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                        Looking up vehicle details from rego...
                      </>
                    ) : (
                      "Vehicle lookup complete (if found, details are prefilled)."
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={makeInput}
                    onChange={(e) => setMakeInput(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={modelInput}
                    onChange={(e) => setModelInput(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VIN (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={vinInput}
                    onChange={(e) => setVinInput(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
            <div className="border-t bg-gray-50 p-3 sm:p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 sm:space-x-0">
                <button
                  type="button"
                  onClick={handleSaveVehicleDetails}
                  disabled={savingVehicle}
                  className="w-full sm:w-auto bg-green-600 text-white py-3 px-4 sm:px-6 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm sm:text-base min-h-[44px]"
                >
                  {savingVehicle ? "Saving..." : "Save and continue to agreement"}
                </button>
              </div>
            </div>
          </>
        ) : isHoldingContractForLookup ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 min-h-[16rem]">
            <span className="inline-block w-8 h-8 mb-4 border-2 border-gray-300 border-t-[#10073E] rounded-full animate-spin" />
            <p className="text-sm font-medium text-gray-800">Looking up vehicle details...</p>
            <p className="text-xs text-gray-500 mt-1 text-center">
              Matching your registration and state before showing the agreement.
            </p>
          </div>
        ) : (
        <>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <div className="flex flex-col lg:flex-row lg:min-h-[28rem]">
          {/* Terms panel */}
          <div className="flex flex-col lg:flex-1 lg:min-h-0 p-3 sm:p-5 lg:border-r lg:border-b-0 border-b">
            <h3 className="text-base sm:text-lg font-semibold mb-2 flex-shrink-0">Terms and Conditions</h3>
            <div className="bg-gray-100 rounded-lg p-2 sm:p-3 flex flex-col lg:flex-1 lg:min-h-0">
              <div className="bg-white rounded p-3 sm:p-4 lg:flex-1 lg:min-h-0 lg:max-h-[calc(95dvh-14rem)] lg:overflow-y-auto">
                <div className="max-w-none text-sm sm:text-base text-gray-900">
                  <h4 className="text-lg sm:text-xl">Priority One Wash Club Subscription Agreement</h4>

                  <div className="bg-gray-50 p-3 sm:p-4 rounded mb-4">
                    <h5 className="font-semibold text-sm sm:text-base">Customer Information</h5>
                    <p className="text-xs sm:text-sm">
                      <strong>Name:</strong> {contractData?.customer?.firstName} {contractData?.customer?.lastName}
                    </p>
                    <p className="text-xs sm:text-sm">
                      <strong>Email:</strong> {contractData?.customer?.email}
                    </p>
                    <p className="text-xs sm:text-sm">
                      <strong>Vehicle:</strong> {displayVehicle?.make} {formatModelForDisplay(displayVehicle?.model)} (
                      {displayVehicle?.rego})
                    </p>
                    <p className="text-xs sm:text-sm">
                      <strong>Product:</strong> {contractData?.product?.name}
                    </p>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded mb-4">
                    <p className="text-xs sm:text-sm text-blue-800">
                      <strong>Note:</strong> If you have agreed to a different term with the P1 team, that is already taken into effect when you sign this contract.
                    </p>
                  </div>

                  <h5 className="text-base sm:text-lg">Priority One Wash Club – Terms and Conditions</h5>
                  <p className="text-xs sm:text-sm">
                    By accepting this subscription agreement, you agree to be bound by the Priority One Wash Club – Terms and Conditions and by the specific pricing, inclusions and minimum term in your Order Confirmation (member portal). Summary of key terms:
                  </p>

                  <ol className="list-decimal list-inside space-y-2 text-xs sm:text-sm">
                    <li>
                      <strong>Program benefits and participating locations:</strong> Participating facilities are listed on priorityonewashclub.com and/or the App. The number and location may change; such changes do not entitle you to terminate or receive a refund except as required by law.
                    </li>
                    <li>
                      <strong>Order Confirmation governs:</strong> The specific terms at time of sale (price, inclusions, minimum term) on your checkout page, receipt, invoice or order confirmation form part of these Terms and prevail to the extent of any inconsistency.
                    </li>
                    <li>
                      <strong>Eligibility and nominated vehicle:</strong> Membership is for a single nominated vehicle and is not transferable unless we agree in writing. Standard memberships are for private, personal use only; commercial use requires approval.
                    </li>
                    <li>
                      <strong>Term and payment:</strong> Your membership has a minimum term with fees payable monthly or annually. After the minimum term it auto-renews until cancelled. You must keep payment details current; we may suspend for non-payment.
                    </li>
                    <li>
                      <strong>Booking and redemption:</strong> Pre-book via the App/Website where available, or attend a participating facility and present your QR code. Redemptions are subject to facility hours, queue and conditions of entry.
                    </li>
                    <li>
                      <strong>Fair-use:</strong> We may apply fair-use protections for abnormal usage inconsistent with normal personal use; we will give written notice and a reasonable opportunity to respond before suspending or cancelling.
                    </li>
                    <li>
                      <strong>Cancellation and early termination:</strong> You may cancel after the minimum term as specified. Early cancellation during the minimum term incurs an Early Termination Fee of $199 plus outstanding amounts. Fees are non-refundable except as required by the Australian Consumer Law.
                    </li>
                    <li>
                      <strong>Member conduct:</strong> You must treat staff with courtesy and comply with facility safety rules. Abusive or unsafe behaviour may result in suspension or termination without refund (subject to law).
                    </li>
                    <li>
                      <strong>Facility terms:</strong> Services are delivered by participating facilities under their own terms (hours, conditions of entry, property/vehicle responsibility, complaints). Complaints should be directed to the facility first; we may assist to mediate.
                    </li>
                    <li>
                      <strong>Liability and Australian Consumer Law:</strong> Nothing in these Terms excludes your ACL rights. Our maximum aggregate liability is limited to the fees you paid for the current minimum term.
                    </li>
                    <li>
                      <strong>Privacy and communications:</strong> We handle personal information in accordance with our Privacy Policy on the Program Website. Marketing consent (SMS/WhatsApp/email) is optional and separate.
                    </li>
                    <li>
                      <strong>Governing law:</strong> These Terms are governed by the laws of New South Wales, Australia, and the parties submit to the non-exclusive jurisdiction of its courts.
                    </li>
                  </ol>

                  <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded">
                    <h5 className="font-semibold text-blue-800 text-sm sm:text-base">Digital Signature Acceptance</h5>
                    <p className="text-blue-700 text-xs sm:text-sm">
                      By providing your digital signature below, you acknowledge that:
                    </p>
                    <ul className="list-disc list-inside text-blue-700 mt-2 text-xs sm:text-sm">
                      <li>You have read and understood the Priority One Wash Club – Terms and Conditions and your Order Confirmation</li>
                      <li>You agree to be bound by the Terms and your Order Confirmation</li>
                      <li>Your digital signature has the same legal effect as a handwritten signature</li>
                      <li>You consent to electronic communication regarding this agreement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Signature Form */}
          <div className="w-full lg:w-80 lg:max-w-[20rem] p-3 sm:p-4 flex-shrink-0 lg:border-l lg:border-gray-200">
            <h3 className="text-sm sm:text-base font-semibold mb-2">Digital Signature</h3>

            {/* Signature Canvas */}
            <div className="mb-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Please sign below:</label>
              <div className="border-2 border-gray-300 rounded-lg p-1.5">
                <canvas
                  ref={signatureCanvasRef}
                  className="border border-gray-200 rounded cursor-crosshair w-full h-24 sm:h-28 lg:h-32"
                  style={{
                    touchAction: "none",
                    display: "block",
                    minHeight: "80px",
                  }}
                />
              </div>
              <button type="button" onClick={clearSignature} className="mt-1 text-xs sm:text-sm text-red-600 hover:text-red-800">
                Clear Signature
              </button>
            </div>

            {/* Acceptance Checkboxes */}
            <div className="space-y-2 mb-2">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 mr-2 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm">
                  I have read and agree to the Priority One Wash Club – Terms and Conditions and to the specific
                  pricing, inclusions and minimum term in my Order Confirmation (member portal).
                </span>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={acceptedSignature}
                  onChange={(e) => setAcceptedSignature(e.target.checked)}
                  className="mt-1 mr-2 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm">
                  I acknowledge that my digital signature has the same legal effect as a handwritten signature.
                </span>
              </label>
            </div>

            <p className="text-[10px] sm:text-xs text-gray-500 text-center leading-snug">
              Legally binding once accepted. IP address and time recorded.
            </p>
          </div>
        </div>
        </div>

        {/* Footer — always visible */}
        <div className="border-t bg-gray-50 p-3 sm:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 sm:space-x-0">
            <button
              onClick={handleAcceptContract}
              disabled={!acceptedTerms || !acceptedSignature || isSubmitting}
              className="w-full sm:w-auto bg-green-600 text-white py-2 px-4 sm:px-6 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
            >
              {isSubmitting
                ? "Processing..."
                : isMultiContractSession && !isLastAgreement
                  ? "Accept & next agreement"
                  : "Accept Contract"}
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ContractAcceptanceModal;
